"use client";
import { useRef, useEffect, useState } from "react";
import { useI18N, useFilters } from "common/hooks";
import { bucketUrlPrefix } from "common/config";
import { cn } from "@/lib/utils";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { Cookie, Coffee, Martini, ForkKnife, ShoppingCart} from "phosphor-react";

// ============================================================================
// Types
// ============================================================================

export interface BusinessType {
  name: string;
  image: string;
}

export interface BusinessTypeDropdownProps {
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Callback when dropdown should close */
  onClose: () => void;
  /** Currently selected business types */
  selectedTypes?: string[];
  /** Callback when business type selection changes */
  onSelectionChange?: (selectedTypes: string[]) => void;
  /** Callback when a single business type is toggled */
  onTypeToggle?: (typeName: string) => void;
  /** Custom className for positioning */
  className?: string;
  /** Optional override for business types (uses useFilters hook by default) */
  businessTypes?: BusinessType[];
}

// ============================================================================
// Component
// ============================================================================

export function BusinessTypeDropdown({
  isOpen,
  onClose,
  selectedTypes = [],
  onSelectionChange,
  onTypeToggle,
  className,
  businessTypes: businessTypesProp,
}: BusinessTypeDropdownProps) {
  const i18n = useI18N();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDark = useDarkMode();

  // Get business types from hook if not provided via props
  const { businessTypes: hookBusinessTypes } = useFilters();
  const businessTypes = businessTypesProp || hookBusinessTypes || [];
  
  // Refs for business type option buttons (for keyboard navigation)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Live region message for screen reader announcements
  const [liveMessage, setLiveMessage] = useState("");

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Check if a type is selected
  const isSelected = (typeName: string) => selectedTypes.includes(typeName);

  // Toggle a business type
  const handleToggle = (typeName: string) => {
    const wasSelected = isSelected(typeName);
    const newCount = wasSelected ? selectedTypes.length - 1 : selectedTypes.length + 1;
    const filterWord = newCount === 1 ? "filter" : "filters";

    setLiveMessage(
      wasSelected
        ? `${typeName} removed. ${newCount} ${filterWord} active.`
        : `${typeName} selected. ${newCount} ${filterWord} active.`,
    );

    // Track analytics
    AnalyticsService.trackGAEventHandler("searchBusinessTypeToggled");
    AnalyticsService.trackMx("searchBusinessTypeToggled", {
      BusinessType: typeName,
      IsSelected: !wasSelected,
    });

    if (onTypeToggle) {
      onTypeToggle(typeName);
    }

    if (onSelectionChange) {
      if (wasSelected) {
        onSelectionChange(selectedTypes.filter((t) => t !== typeName));
      } else {
        onSelectionChange([...selectedTypes, typeName]);
      }
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    setLiveMessage("All business type filters cleared.");

    // Track analytics
    AnalyticsService.trackGAEventHandler("searchBusinessTypeFilterCleared");
    AnalyticsService.trackMx("searchBusinessTypeFilterCleared", {
      ClearedTypesCount: selectedTypes.length,
    });

    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  // Handle keyboard navigation for listbox options
  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        targetIndex = index < businessTypes.length - 1 ? index + 1 : index;
        break;
      case "ArrowUp":
        e.preventDefault();
        targetIndex = index > 0 ? index - 1 : index;
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = businessTypes.length - 1;
        break;
      case " ": // Space key
      case "Enter":
        e.preventDefault();
        handleToggle(businessTypes[index].name);
        return;
      default:
        return;
    }

    if (targetIndex !== null && optionRefs.current[targetIndex]) {
      optionRefs.current[targetIndex]?.focus();
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={cn(
        "bg-bg-brand-secondary-default rounded-xl shadow-lg p-4 w-full md:w-[380px]",
        "border border-neutral-100 max-h-[60vh] overflow-y-auto",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="body-l-semibold text-fg-neutral-strong" id="business-type-label">
          {i18n.get("businessType")}
        </h3>
        {selectedTypes.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="label-s-medium text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue"
            aria-label={`${i18n.get("clearAll")} business type filters`}
          >
            {i18n.get("clearAll")}
          </button>
        )}
      </div>

      {/* Business Type Tags Grid */}
      <div
        className="flex flex-wrap gap-2"
        role="listbox"
        aria-labelledby="business-type-label"
        aria-multiselectable="true"
      >
        {businessTypes.map((item, index) => (
          <button
            key={item.name}
            ref={(el) => (optionRefs.current[index] = el)}
            type="button"
            onClick={() => handleToggle(item.name)}
            onKeyDown={(e) => handleOptionKeyDown(e, index)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full transition-colors focus-blue border border-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover",
              isSelected(item.name)
                ? "bg-bg-brand-tertiary-active"
                : "bg-bg-brand-tertiary-default",
            )}
            role="option"
            aria-selected={isSelected(item.name)}
            aria-label={item.name}
          >
            {/* Business Type Icon */}
            <div
              className="w-5 h-5 flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >             
              {item.name === "Bakery" && <Cookie size={20} className="text-fg-brand-subtle" />}
              {item.name === "Cafe" && <Coffee size={20} className="text-fg-brand-subtle" />}
              {item.name === "Bar" && <Martini size={20} className="text-fg-brand-subtle" />}
              {item.name === "Restaurant" && <ForkKnife size={20} className="text-fg-brand-subtle" />}
              {item.name === "Supermarket" && <ShoppingCart size={20} className="text-fg-brand-subtle" />}
            </div>

            {/* Business Type Name */}
            <span
              className={cn(
                "label-s-medium whitespace-nowrap",
                isSelected(item.name)
                  ? "text-fg-brand-subtle"
                  : "text-fg-brand-subtle",
              )}
            >
              {item.name}
            </span>
          </button>
        ))}
      </div>

      {/* No business types message */}
      {businessTypes.length === 0 && (
        <p className="text-center py-8 label-m-medium text-fg-neutral-subtle">
          {i18n.get("noBusinessTypesAvailable")}
        </p>
      )}

      {/* Visually hidden live region for screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMessage}
      </div>
    </div>
  );
}

export default BusinessTypeDropdown;
